import React, { useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import ViewMoreCellMC from './ViewMoreCellMC';

/**
 * AccordionMC component
 *
 * @param {string} id - **Required** - String that defines the identifier for the accordion.
 * @param {string} label - **Required** - String that defines the label for the accordion.
 * @param {Function} onSelect - **Required** - Function to be called when an option is selected. It receives the selected option id as a parameter.
 * @param {string} color - **Optional** - String that defines the color for the accordion dot. Must be in the format #AAAAAA.
 * @param {Array<IOptionProps>} options - **Required** - Array of objects that defines the options to be displayed in the accordion.
 *
 * @typedef {Object} IOptionProps
 * @property {number} id - **Required** - Number that defines the option identifier.
 * @property {string} title - **Required** - String that defines the option title.
 * @property {string} subtitle - **Optional** - String that defines the option subtitle.
 * @property {string} icon - **Optional** - String that defines the option icon.
 * @property {string} text - **Optional** - String that defines the option text.
 * @property {boolean} defaultOpen - **Optional** - Boolean that defines if the option is open by default.
 * @property {string} defaultOption - **Optional** - String that defines the default option id.
 * @property {string | null} optionId - **Optional** - String that defines the option id from the parent component.
 * @property {function} onToggleOpen(isOpen) - **Optional** - Function to be called when the option is selected.
 */
const AccordionMC = ({
  id,
  label,
  onSelect,
  color,
  options = [],
  defaultOpen = false,
  onToggleOpen,
  defaultOption,
  optionId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const toggleAccordion = () => {
    if (onToggleOpen) {
      onToggleOpen(!isOpen);
    }
    setIsOpen(!isOpen);
  };

  const selectedOptionId = optionId !== undefined ? optionId : selectedOption;

  const handleOptionClick = (clickedOptionId) => {
    if (selectedOptionId === null || clickedOptionId !== selectedOptionId) {
      setSelectedOption(clickedOptionId);
      onSelect(clickedOptionId);
    }
  };

  const dotStyle = {
    backgroundColor: color
  };

  React.useEffect(() => {
    if (defaultOpen) {
      setIsOpen(true);
    }
    if (defaultOption) {
      setSelectedOption(defaultOption.id);
    }
  }, []);

  return (
    <div className="tw-wrapper">
      <button
        type="button"
        className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer tw-py-2 tw-w-full"
        onClick={toggleAccordion}
      >
        <div className="tw-flex tw-items-center">
          {isOpen ? (
            <ChevronUpIcon className="tw-w-4 tw-h-4 tw-mr-3" />
          ) : (
            <ChevronDownIcon className="tw-w-4 tw-h-4 tw-mr-3" />
          )}
          {color && <div className="tw-w-2 tw-h-2 tw-rounded-full tw-mr-2" style={dotStyle} />}
          <label htmlFor={id} className="tw-text-base tw-m-0 tw-text-gray-900">
            {label}
          </label>
        </div>
      </button>

      <div className="tw-border-t tw-border-gray-200 "></div>
      {isOpen && (
        <ul id={`${id}-content`} className="">
          {options.map((option) => (
            <li
              key={option.id}
              className={`tw-flex tw-flex-col tw-py-3 tw-cursor-pointer tw-select-none ${
                selectedOptionId === option.id ? 'tw-bg-gray-50' : ''
              }`}
              onClick={() => handleOptionClick(option.id)}
            >
              <div className="tw-flex tw-w-full tw-px-3">
                <div className="tw-flex tw-flex-col tw-flex-1 tw-min-w-0 tw-mr-3">
                  <div className="tw-flex tw-w-full tw-justify-start">
                    <span className="tw-font-bold tw-text-xs tw-break-words">{option.title}</span>
                  </div>
                  <div className="tw-flex tw-w-full tw-items-start tw-mt-1">
                    {option.icon && (
                      <span className={`tw-text-xs tw-items-center tw-mr-2 ${option.icon}`} />
                    )}
                    {option.subtitle && (
                      <span className="tw-flex tw-flex-col tw-text-xs tw-text-gray-500">
                        {option.subtitle}
                      </span>
                    )}
                  </div>
                </div>

                <div className="tw-flex tw-justify-center tw-w-8">
                  <div className="tw-w-8 tw-h-8 tw-border tw-border-gray-200 tw-bg-gray-200 tw-rounded tw-flex tw-items-center tw-justify-center">
                    {selectedOptionId === option.id && (
                      <CheckCircleIcon className="tw-w-5 tw-h-5 tw-text-blue-500" />
                    )}
                  </div>
                </div>
              </div>

              {option.text && (
                <div className="tw-w-full tw-px-3 tw-mt-2">
                  <ViewMoreCellMC text={option.text} limit={85} className="tw-text-xs" />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AccordionMC;
